import { TradutorAssistente } from '../../dominio/assistente.interface'
import { CHAVE_POR_FERRAMENTA } from './seloDeFerramenta'

/**
 * E7 — o tratamento da espera (R5).
 *
 * O que foi MEDIDO nesta onda: mediana de ~25s por turno, p95 de ~38s, e a
 * espera inteira representada por uma frase fixa — "● ● ● Processando sua
 * solicitação…". Tudo acima de 4s já pede tratamento; o teto confortável com
 * status genérico é ~10s. Estamos 2-4x acima disso em TODOS os turnos.
 *
 * E o tempo não é do modelo: subir o provider para `gemini-3.7-flash` deixou a
 * mediana em 27,35s. Quem manda são as rodadas de tool-call. Então não há
 * "esperar o modelo ficar rápido" — o que dá para consertar é a espera.
 *
 * Duas metades, as duas puras:
 *
 * 1. `fraseDoEvento` — quando o servidor DIZ o que está fazendo, a interface
 *    repete isso na língua da pessoa. O payload já publica `ferramenta`
 *    (`WireEventoStream.payload`) e ninguém o lia: o passo dizia "Consultando
 *    informações…" tendo em mãos "listar_feriados". Nome humano sai do MESMO
 *    mapa do selo (`seloDeFerramenta`), nunca de um segundo mapa que
 *    divergiria no primeiro bump do catálogo.
 *
 * 2. `faseDeSilencio` — quando o servidor NÃO diz nada, o relógio diz. Degraus
 *    em 10s/20s/30s, para que nenhuma espera passe de 10 segundos sem
 *    informação nova. As frases não prometem prazo que ninguém pode cumprir;
 *    elas dizem o que se sabe — que ainda está em curso, e que este tamanho de
 *    consulta leva o que leva.
 */

/** Degraus do relógio, em milissegundos desde o último sinal do servidor. */
export const DEGRAUS_DE_SILENCIO_MS = [10_000, 20_000, 30_000] as const

const CHAVE_POR_DEGRAU: Record<number, string> = {
  10_000: 'assistentePontotel.progresso.espera.dezSegundos',
  20_000: 'assistentePontotel.progresso.espera.vinteSegundos',
  30_000: 'assistentePontotel.progresso.espera.trintaSegundos'
}

/** Fallback por tipo de evento — a régua de hoje, preservada. */
export const CHAVE_POR_TIPO_DE_EVENTO: Record<string, string> = {
  interacao_aceita: 'assistentePontotel.progresso.aceita',
  processamento_iniciado: 'assistentePontotel.progresso.iniciado',
  ferramenta_iniciada: 'assistentePontotel.progresso.ferramenta',
  ferramenta_concluida: 'assistentePontotel.progresso.finalizando'
}

export const CHAVE_DO_PASSO_GENERICO = 'assistentePontotel.progresso.mensagem'

/**
 * Famílias de ferramenta reconhecidas pelo PREFIXO do identificador, porque é
 * o prefixo que o servidor MCP usa como convenção de nomeação (`consultar_`,
 * `pre_visualizar_`, `confirmar_`). Casar por prefixo — e não por lista
 * fechada de nomes — é o que faz a frase continuar certa quando o catálogo
 * ganha a próxima consulta sem passar por aqui.
 *
 * A ordem importa: `pre_visualizar_` e `confirmar_` são conferidos antes de
 * qualquer coisa que pareça leitura.
 */
const FAMILIAS: Array<{ prefixos: string[], chave: string, nomeada: boolean }> = [
  { prefixos: ['pre_visualizar_'], chave: 'assistentePontotel.progresso.espera.conferindo', nomeada: false },
  { prefixos: ['confirmar_'], chave: 'assistentePontotel.progresso.espera.aplicando', nomeada: false },
  { prefixos: ['propor_navegacao'], chave: 'assistentePontotel.progresso.espera.abrindoTela', nomeada: false },
  { prefixos: ['propor_formulario'], chave: 'assistentePontotel.progresso.espera.preparandoFormulario', nomeada: false },
  {
    prefixos: ['listar_', 'consultar_', 'descobrir_', 'acao_de_'],
    chave: 'assistentePontotel.progresso.espera.consultando',
    nomeada: true
  }
]

function textoJaPronto (payload: Record<string, unknown> | null | undefined): string | null {
  const texto = payload?.texto ?? payload?.mensagem
  return typeof texto === 'string' && texto.trim() ? texto : null
}

function identificadorDaFerramenta (payload: Record<string, unknown> | null | undefined): string | null {
  const ferramenta = payload?.ferramenta
  if (typeof ferramenta !== 'string') return null
  const limpo = ferramenta.trim()
  return limpo || null
}

/**
 * Nome humano da ferramenta, ou `null` quando ela não está no mapa do selo.
 * `null` é resposta legítima: identificador cru nunca vai para a tela — é o
 * mesmo compromisso do QW6, e um `snake_case` na espera seria pior do que a
 * frase genérica, porque a pessoa está olhando para ele.
 */
export function nomeAmigavelDaFerramenta (
  ferramenta: string | null | undefined,
  t: TradutorAssistente
): string | null {
  if (!ferramenta) return null
  const chave = CHAVE_POR_FERRAMENTA[ferramenta]
  return chave ? String(t(chave)) : null
}

/**
 * A frase que a pessoa lê para ESTE evento. Precedência:
 *
 * 1. texto que o próprio servidor mandou (`payload.texto`/`payload.mensagem`);
 * 2. a família da ferramenta publicada em `payload.ferramenta`;
 * 3. o tipo do evento;
 * 4. o genérico.
 *
 * O degrau 2 é o que estava faltando: o campo já vinha e ninguém o lia.
 */
export function fraseDoEvento (
  tipo: string,
  payload: Record<string, unknown> | null | undefined,
  t: TradutorAssistente
): string {
  const pronto = textoJaPronto(payload)
  if (pronto) return pronto

  const ferramenta = identificadorDaFerramenta(payload)
  if (ferramenta) {
    const familia = FAMILIAS.find(({ prefixos }) => prefixos.some(prefixo => ferramenta.startsWith(prefixo)))
    if (familia) {
      if (!familia.nomeada) return String(t(familia.chave))
      const nome = nomeAmigavelDaFerramenta(ferramenta, t)
      // Sem nome humano a frase com `{nome}` ficaria com o buraco à mostra;
      // o passo genérico de ferramenta é a sobra digna.
      if (nome) return String(t(familia.chave, { nome }))
    }
  }

  return String(t(CHAVE_POR_TIPO_DE_EVENTO[tipo] || CHAVE_DO_PASSO_GENERICO))
}

/**
 * O degrau de silêncio alcançado, ou `null` enquanto a espera ainda cabe no
 * que uma pessoa tolera sem notícia. Devolve o degrau em MILISSEGUNDOS (a
 * identidade do passo) junto com a chave, para que quem chama saiba se este
 * degrau já foi anunciado e não repita a mesma frase a cada tique.
 */
export function faseDeSilencio (
  msDesdeUltimoEvento: number
): { degrau: number, chave: string } | null {
  if (!Number.isFinite(msDesdeUltimoEvento)) return null
  let alcancado: number | null = null
  for (const degrau of DEGRAUS_DE_SILENCIO_MS) {
    if (msDesdeUltimoEvento >= degrau) alcancado = degrau
  }
  if (alcancado === null) return null
  return { degrau: alcancado, chave: CHAVE_POR_DEGRAU[alcancado] }
}

/** Segundos inteiros decorridos, para o rodapé discreto do passo ("· 18s"). */
export function segundosDecorridos (msDecorridos: number): number {
  if (!Number.isFinite(msDecorridos) || msDecorridos < 0) return 0
  return Math.floor(msDecorridos / 1000)
}
